import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Send, Lock, Unlock, MessageSquare } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

function PresenceDot({ presence }) {
  const color = presence === "green" ? "#3e6a3c" : presence === "yellow" ? "#c9a84c" : "#7c2a2a";
  return (
    <span
      title={presence}
      style={{
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: "50%",
        background: color,
        boxShadow: `0 0 0 2px ${color}33`,
        verticalAlign: "middle",
      }}
    />
  );
}

export default function LodgeChatPage() {
  const { t, i18n } = useTranslation();
  const locale =t("lodgeChat.en_gb");
  const { user } = useAuth();
  const [chats, setChats] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const loadChats = async () => {
    try {
      const { data } = await api.get("/chats");
      setChats(data);
      if (data.length > 0 && !active) setActive(data[0]);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const loadMessages = async (lodgeId) => {
    try {
      const { data } = await api.get(`/chats/${lodgeId}/messages`);
      setMessages(data);
      setTimeout(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
      }, 50);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    loadChats();
    const t = setInterval(loadChats, 15000);
    return () => clearInterval(t);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!active?.lodge_id) return;
    loadMessages(active.lodge_id);
    const t = setInterval(() => loadMessages(active.lodge_id), 12000);
    return () => clearInterval(t);
  }, [active?.lodge_id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || !active) return;
    setBusy(true);
    try {
      await api.post(`/chats/${active.lodge_id}/messages`, { body });
      setDraft("");
      await loadMessages(active.lodge_id);
      await loadChats();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleBlock = async () => {
    if (!active) return;
    try {
      const action = active.blocked_by_me ? "unblock" : "block";
      await api.post(`/chats/${active.lodge_id}/${action}`);
      toast.success(action === "block" ? t("chat.blocked") : t("chat.unblocked"));
      await loadChats();
      const updated = await api.get("/chats");
      const cur = updated.data.find((c) => c.lodge_id === active.lodge_id);
      if (cur) setActive(cur);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (user?.role !== "admin") {
    return (
      <div data-testid="chat-no-access">
        <h1 className="page-title">{t("chat.title")}</h1>
        <p className="page-subtitle">{t("chat.adminOnly")}</p>
      </div>
    );
  }

  return (
    <div data-testid="lodge-chat-page">
      <div>
        <h1 className="page-title">{t("chat.title")}</h1>
        <p className="page-subtitle">{t("chat.subtitle")}</p>
      </div>
      <div className="gold-divider my-6" />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(260px, 320px) 1fr",
          gap: "1rem",
          minHeight: "70vh",
        }}
        className="chat-grid"
      >
        {/* Lodge list */}
        <aside style={{ background: "#fff", border: "1px solid var(--cream-200)" }} data-testid="chat-lodges-list">
          <div className="section-heading" style={{ padding: "0.7rem 1rem", borderBottom: "1px solid var(--cream-200)" }}>
            {t("chat.lodges")} · {chats.length}
          </div>
          {chats.length === 0 && (
            <div style={{ padding: "1rem", color: "#858481", fontStyle: "italic", fontSize: "0.85rem" }}>
              {t("chat.noLodges")}
            </div>
          )}
          {chats.map((c) => (
            <button
              key={c.lodge_id}
              type="button"
              onClick={() => setActive(c)}
              data-testid={`chat-lodge-${c.lodge_id}`}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "0.7rem 1rem",
                borderBottom: "1px solid var(--cream-200)",
                background: active?.lodge_id === c.lodge_id ? "#fdf4e0" : "#fff",
                color: "#0d1b2a",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <PresenceDot presence={c.presence} />
                <span style={{ fontFamily: "Cinzel, serif", fontSize: "0.88rem", flex: 1 }}>{c.name}</span>
                {c.unread > 0 && (
                  <span
                    className="notif-dot"
                    style={{ position: "static", color: "#fff", background: "#7c2a2a", padding: "0 6px" }}
                    data-testid={`chat-unread-${c.lodge_id}`}
                  >
                    {c.unread}
                  </span>
                )}
              </div>
              <div className="kv-label" style={{ marginTop: 3, fontSize: "0.62rem" }}>
                {c.city || c.country}
                {c.blocked_by_me && <span style={{ color: "#7c2a2a", marginLeft: 6 }}>· {t("chat.blockedByMe")}</span>}
                {c.blocked_by_them && <span style={{ color: "#7c2a2a", marginLeft: 6 }}>· {t("chat.blockedByThem")}</span>}
              </div>
              {c.last_message && (
                <div className="kv-label" style={{ marginTop: 3, fontStyle: "italic", fontSize: "0.7rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {c.last_message.body}
                </div>
              )}
            </button>
          ))}
        </aside>

        {/* Conversation */}
        <section style={{ background: "#fff", border: "1px solid var(--cream-200)", display: "flex", flexDirection: "column" }} data-testid="chat-conversation">
          {!active ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#858481" }}>
              <MessageSquare size={48} style={{ marginRight: 8 }} />
              {t("chat.pickLodge")}
            </div>
          ) : (
            <>
              <div
                style={{
                  borderBottom: "1px solid var(--cream-200)",
                  padding: "0.7rem 1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <PresenceDot presence={active.presence} />
                <div style={{ fontFamily: "Cinzel, serif", color: "#0d1b2a", flex: 1 }}>{active.name}</div>
                <button
                  type="button"
                  className={active.blocked_by_me ? "btn-outline-gold" : "btn-danger"}
                  onClick={toggleBlock}
                  data-testid="chat-block-btn"
                  style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                >
                  {active.blocked_by_me ? <Unlock size={11} /> : <Lock size={11} />}
                  {active.blocked_by_me ? t("chat.unblockBtn") : t("chat.blockBtn")}
                </button>
              </div>

              {active.blocked_by_them && (
                <div style={{ background: "#f6e0e0", border: "1px solid #d9b9b9", color: "#7c2a2a", padding: "0.6rem 0.9rem", margin: "0.7rem 1rem 0", textAlign: "center" }}>
                  {t("chat.blockedByThemBanner")}
                </div>
              )}

              <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "1rem", display: "flex", flexDirection: "column", gap: "0.55rem" }} data-testid="chat-messages">
                {messages.length === 0 && (
                  <div style={{ color: "#858481", fontStyle: "italic", textAlign: "center", marginTop: "1.5rem" }}>{t("chat.noMessages")}</div>
                )}
                {messages.map((m) => {
                  const mine = m.sender_lodge_id !== active.lodge_id;
                  return (
                    <div
                      key={m.id}
                      data-testid={`chat-msg-${m.id}`}
                      style={{
                        alignSelf: mine ? "flex-end" : "flex-start",
                        maxWidth: "78%",
                        background: mine ? "#0d1b2a" : "#fdf4e0",
                        color: mine ? "#fff" : "#0d1b2a",
                        border: mine ? "1px solid #0d1b2a" : "1px solid #c9a84c",
                        padding: "0.55rem 0.85rem",
                        fontSize: "0.92rem",
                      }}
                    >
                      <div style={{ whiteSpace: "pre-wrap" }}>{m.body}</div>
                      <div className="kv-label" style={{ color: mine ? "rgba(232,201,122,0.8)" : "#9a7530", marginTop: 4, fontSize: "0.6rem" }}>
                        {m.sender_name} · {new Date(m.created_at).toLocaleString(locale, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {!active.blocked_by_them && !active.blocked_by_me && (
                <form onSubmit={send} style={{ display: "flex", gap: 8, padding: "0.7rem", borderTop: "1px solid var(--cream-200)" }}>
                  <input
                    type="text"
                    className="input-lodge"
                    placeholder={t("chat.placeholder")}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    data-testid="chat-input"
                    style={{ flex: 1 }}
                  />
                  <button type="submit" className="btn-primary-gold" disabled={busy || !draft.trim()} data-testid="chat-send-btn">
                    <Send size={13} />
                  </button>
                </form>
              )}
              {active.blocked_by_me && (
                <div style={{ padding: "0.8rem", borderTop: "1px solid var(--cream-200)", color: "#7c2a2a", fontStyle: "italic", textAlign: "center" }}>
                  {t("chat.youBlockedBanner")}
                </div>
              )}
            </>
          )}
        </section>
      </div>
      <style>{`
        @media (max-width: 720px) {
          .chat-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
